import React from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { Store } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { format as formatDate } from 'date-fns';
import type { CreateAuditSetupViewProps } from '../../../../types/auditFormTypes';

const CreateAuditSetupView: React.FC<CreateAuditSetupViewProps> = ({
  loading,
  error,
  stores,
  selectedStore,
  shiftDate,
  shiftStartTime,
  shiftEndTime,
  fetchingPrevious,
  onStoreChange,
  onShiftDateChange,
  onShiftStartTimeChange,
  onShiftEndTimeChange,
  onContinue,
  onBackToAuditList,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={1} sx={{ p: 2, borderBottom: '1px solid #ddd' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
              <Link
                color="rgba(44, 108, 160, 1)"
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  onBackToAuditList();
                }}
                sx={{ cursor: 'pointer' }}
              >
                Audit
              </Link>
              <Typography color="text.primary" fontWeight={600}>Create New Audit</Typography>
            </Breadcrumbs>
            <Typography variant="h5" component="h1" fontWeight={600}>
              Create New Audit
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select store and shift details to prepare the audit form.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Store Selection
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="store-select-label">Store</InputLabel>
              <Select
                labelId="store-select-label"
                id="store-select"
                value={selectedStore}
                label="Store"
                onChange={(event) => onStoreChange(String(event.target.value))}
                sx={{
                  '& .MuiSelect-select': {
                    padding: '25px 14px',
                  },
                }}
              >
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Store sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {store.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {store.state} • {store.city}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Shift Details
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <DatePicker
                  format="MM/dd/yyyy"
                  label="Shift Date"
                  value={shiftDate ? new Date(shiftDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      onShiftDateChange(formatDate(newValue, 'yyyy-MM-dd'));
                      return;
                    }

                    onShiftDateChange('');
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                    },
                  }}
                  openTo="day"
                  views={['year', 'month', 'day']}
                  minDate={new Date('1900-01-01')}
                  maxDate={new Date('2100-12-31')}
                  closeOnSelect
                  showDaysOutsideCurrentMonth
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TimePicker
                    label="Shift Start Time"
                    value={shiftStartTime ? new Date(`2000-01-01T${shiftStartTime}`) : null}
                    onChange={(newValue) => {
                      if (newValue) {
                        const hours = newValue.getHours().toString().padStart(2, '0');
                        const minutes = newValue.getMinutes().toString().padStart(2, '0');
                        onShiftStartTimeChange(`${hours}:${minutes}:00`);
                        return;
                      }

                      onShiftStartTimeChange('');
                    }}
                    ampm={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        sx: { flex: 1 },
                      },
                    }}
                  />

                  <TimePicker
                    label="Shift End Time"
                    value={shiftEndTime ? new Date(`2000-01-01T${shiftEndTime}`) : null}
                    onChange={(newValue) => {
                      if (newValue) {
                        const hours = newValue.getHours().toString().padStart(2, '0');
                        const minutes = newValue.getMinutes().toString().padStart(2, '0');
                        onShiftEndTimeChange(`${hours}:${minutes}:00`);
                        return;
                      }

                      onShiftEndTimeChange('');
                    }}
                    ampm={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        sx: { flex: 1 },
                      },
                    }}
                  />
                </Box>
              </Box>
            </LocalizationProvider>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={onContinue}
            disabled={!selectedStore || !shiftDate || !shiftStartTime || !shiftEndTime || fetchingPrevious}
            sx={{
              minWidth: 220,
              background: '#2C6CA0',
              '&:hover': {
                background: '#2C6CA0',
              },
            }}
          >
            {fetchingPrevious ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} color="inherit" />
                Preparing Audit Form...
              </>
            ) : (
              'Continue to Audit Form'
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateAuditSetupView;
